export interface GameSettings {
  initialSnakeSpeed: number;
  minSnakeSpeed: number;
  maxSnakeSpeed: number;
  minSnakeSegments: number;
  regularFoodPoints: number;
  racingFoodPoints: number;
  racingFoodIntervalMs: number;
  racingFoodDurationMs: number;
  foodDistanceMin: number;
  foodDistanceMax: number;
  foodsPerBlock: number;
  blockWarningDurationMs: number;
  blockAttackCooldownMs: number;
  deathScorePenalty: number;
  peakLengthBonus: number;
  longestSnakeBonus: number;
  longestSnakeMinLength: number;
  maxPlayers: number;
  gameDuration: number;
  suddenDeathCountdownMs: number;
  suddenDeathRespawnSize: number;
  suddenDeathTargetSize: number;
  suddenDeathRacingFoodIntervalMs: number;
  suddenDeathBlockInitialIntervalMs: number;
  suddenDeathBlockIntervalDecay: number;
  // Power-ups — spawn behaviour
  powerupSpawnIntervalMs: number;
  powerupLifetimeMs: number;
  powerupMaxEffects: number;
  // Speed Up
  speedUpDurationMs: number;
  speedUpDelta: number;
  // Speed Down
  speedDownDurationMs: number;
  speedDownDelta: number;
  // Bulldozer
  bulldozerDurationMs: number;
  // Flash
  flashDurationMs: number;
}

export enum Direction {
  UP = 'UP',
  DOWN = 'DOWN',
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export enum PowerUpType {
  SPEED_UP = 'SPEED_UP',
  SPEED_DOWN = 'SPEED_DOWN',
  BULLDOZER = 'BULLDOZER',
  FLASH = 'FLASH',
}

/**
 * Whether a power-up acts on the player who uses it (blue) or is sent to an
 * opponent (red). Decided when the pickup spawns on the board.
 */
export enum PowerUpVariant {
  PLAYER = 'PLAYER',
  OPPONENT = 'OPPONENT',
}

/** A power-up held in a player's inventory slot. */
export interface CollectedPowerUp {
  type: PowerUpType;
  variant: PowerUpVariant;
}

/** A power-up pickup tile present on a player's board. */
export interface Pickup {
  position: Position;
  type: PowerUpType;
  variant: PowerUpVariant;
  /** Timestamp (ms) when the pickup despawns if not collected. */
  expiresAt: number;
}

/** A timed power-up effect currently active on a player. */
export interface ActiveEffect {
  type: PowerUpType;
  variant: PowerUpVariant;
  /** Timestamp (ms) when the effect ends. */
  expiryTime: number;
  /** Total duration of this effect in ms — used by the client to render progress. */
  durationMs: number;
}

export enum FoodType {
  REGULAR = 'regular',
  RACING = 'racing',
}

export enum GameStatus {
  WAITING = 'WAITING',
  STARTING = 'STARTING',
  RUNNING = 'RUNNING',
  FINISHED = 'FINISHED',
}

export interface Position {
  x: number;
  y: number;
}

export interface SnakeSegment {
  position: Position;
}

export interface Snake {
  segments: SnakeSegment[];
  direction: Direction;
  nextDirection?: Direction;
  speed: number;
}

export interface Food {
  position: Position;
  type: FoodType;
  powerUpType?: PowerUpType;
}

export interface RacingFoodState {
  foods: Record<string, Food>;
  spawnTime: number;
  collected: boolean;
}

export interface Obstacle {
  position: Position;
  sentBy: string; // player ID of the attacker
  placementTime: number;
  highlighted: boolean; // true during 3-second warning period
}

export interface Player {
  id: string;
  snake: Snake;
  score: number;
  storedBlocks: number;
  blockProgress: number;
  foodDistanceIndex: number;
  maxLength: number;
  foodCollected: number;
  racingFoodWins: number;
  deathCount: number;
  powerUps: CollectedPowerUp[];
  selectedPowerUpIndex: number;
  effects: ActiveEffect[];
  /** Power-up pickup currently on this player's board, if any. */
  pickup: Pickup | null;
  foods: Food[];
  obstacles: Obstacle[];
  lastProcessedInput: number;
  disconnected: boolean; // true while the player is disconnected but within grace period
  /** True when the player has been permanently eliminated during sudden death (no respawn). */
  eliminated: boolean;
  /** Transient: positions of blocks destroyed this tick via the constrictor mechanic. Reset each tick. */
  destroyedBlocks: Position[];
  /** Timestamp (ms) until which obstacles cannot kill this snake after a respawn. 0 when unprotected. */
  spawnProtectionUntil: number;
}

export interface SuddenDeathState {
  /** Countdown/active phase of this round. */
  phase: 'countdown' | 'active';
  /** Which round of sudden death this is (1-based). Displayed as 'Sudden Death 2' when > 1. */
  round: number;
  /** Timestamp (ms) when the countdown ends and active play begins. */
  countdownEndsAt: number;
  /** Player IDs participating in this sudden death round. */
  participantIds: string[];
  /** Player IDs eliminated during this round (no respawn). */
  eliminatedIds: string[];
  /** Physical snake length a participant must reach to win. Increments on simultaneous ties. */
  targetSize: number;
  /** Timestamp for the next server-spawned SD block. */
  nextBlockAt: number;
  /** Current interval (ms) between SD block spawns; decreases by decay factor each spawn. */
  blockIntervalMs: number;
}

export interface GameState {
  id: string;
  status: GameStatus;
  players: Record<string, Player>;
  racingFood: RacingFoodState | null;
  longestSnakeHolder: string | null;
  longestSnakeThreshold: number;
  distanceSequence: number[];
  startTime?: number;
  endTime?: number;
  timeRemaining: number;
  gridSize: {
    width: number;
    height: number;
  };
  settings: GameSettings;
  /** Present when sudden death is active; null otherwise. */
  suddenDeath: SuddenDeathState | null;
}

export interface PlayerBoardState {
  snake: Position[];
  food: Position;
  racingFood: Position | null;
  score: number;
  blockProgress: number;
  storedBlocks: number;
  direction: Direction;
  lastProcessedInput: number;
  hasLongestSnakeTitle: boolean;
  obstacles: {
    position: Position;
    highlighted: boolean;
    placementTime: number;
  }[];
  /** Positions of blocks destroyed this tick via the constrictor mechanic. Used for visual feedback only. */
  destroyedObstacles: Position[];
  /** Power-up inventory slots (max 2: active + reserve). */
  powerUps: CollectedPowerUp[];
  /** Index into `powerUps` marking the active slot. */
  selectedPowerUpIndex: number;
  /** Timed power-up effects currently active on this player. */
  effects: ActiveEffect[];
  /** Power-up pickup currently on this board, if any. */
  pickup: Pickup | null;
  /** Timestamp (ms) until which this snake is spawn-protected. Rendered as a blink. 0 when unprotected. */
  spawnProtectionUntil: number;
}
